import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import { useAuth } from '../contexts/AuthContext';
import materiaService from '../services/MateriaService';
import areaService from '../services/AreaService';
import { isGptClassicEnabled as hasGptClassicFeature } from '../utils/adminMenu';
import { MATERIA_TIPO_GRADO, MATERIA_TIPO_OPTIONS, normalizeMateriaTipo } from '../utils/materiaTypes';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';

const AdminCreateMateria = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const stateRegionalId = location.state?.idRegional || null;
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional';
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(RegionalService.getFeatureFlags()), []);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);
  const [areas, setAreas] = useState([]);
  const [anios, setAnios] = useState([]);

  const [form, setForm] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    tipoMateria: MATERIA_TIPO_GRADO,
    idArea: '',
    idAnio: '',
    idRegional: activeRegionalId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const list = await areaService.getAreasActivas(activeRegionalId);
        setAreas(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar las áreas.');
        setMessageType('error');
      }
    };
    fetchAreas();
    const fetchAnios = async () => {
      try {
        const list = await materiaService.getAniosActivos();
        setAnios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar los años:', error);
      }
    };
    fetchAnios();
    if (activeRegionalId && !form.idRegional) {
      setForm((prev) => ({ ...prev, idRegional: activeRegionalId }));
    }
  }, [activeRegionalId, form.idRegional]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre de la materia.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área para asociar la materia.');
      setMessageType('error');
      return;
    }
    if (!form.tipoMateria) {
      setMessage('Selecciona el tipo de materia.');
      setMessageType('error');
      return;
    }
    if (!form.idRegional) {
      setMessage('No se pudo determinar el regional activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando materia...');
    setMessageType('info');
    try {
      await materiaService.createMateria({
        nombre: form.nombre,
        codigo: form.codigo,
        descripcion: form.descripcion,
        tipoMateria: normalizeMateriaTipo(form.tipoMateria),
        idRegional: form.idRegional,
        idArea: form.idArea,
        idAnio: form.idAnio || null
      });
      setMessage('Materia creada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'materias' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear la materia.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            activeItemId="materias"
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de materia</h1>
            <p className="page-subtitle">Crea una nueva materia paral regional {activeRegionalName}.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <div className="user-menu">
              <button className="user-menu-trigger" onClick={logout}>
                <div className="user-avatar">
                  <i className="fas fa-user-shield"></i>
                </div>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-book" />
                <span>Datos de la materia</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos de la materia. Selecciona el área obligatoria para este regional; el año es opcional.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Materia X"
                  />
                </div>
                <div className="form-field">
                  <label>Código</label>
                  <input
                    type="text"
                    value={form.codigo || ''}
                    onChange={(e) => handleChange('codigo', e.target.value)}
                    placeholder="Ej: AED-101"
                  />
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción de la materia"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>
                <div className="form-field">
                  <label>Tipo de materia</label>
                  <select
                    value={form.tipoMateria}
                    onChange={(e) => handleChange('tipoMateria', e.target.value)}
                  >
                    {MATERIA_TIPO_OPTIONS.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Área (obligatorio)</label>
                  <select
                    value={form.idArea}
                    onChange={(e) => handleChange('idArea', e.target.value)}
                  >
                    <option value="">Selecciona un área</option>
                    {areas.map((area) => (
                      <option key={area.id || area.Id} value={area.id || area.Id}>
                        {area.nombre || area.name || area.descripcion || area.detalle || 'Área'}
                      </option>
                    ))}
                  </select>
                  {areas.length === 0 && (
                    <p style={{ color: '#ea580c', margin: '0.35rem 0 0 0' }}>
                      No hay áreas disponibles para este regional.
                    </p>
                  )}
                </div>
                <div className="form-field">
                  <label>Año</label>
                  <select
                    value={form.idAnio || ''}
                    onChange={(e) => handleChange('idAnio', e.target.value)}
                  >
                    <option value="">Sin año</option>
                    {anios.map((anio) => (
                      <option key={anio.id || anio.Id} value={anio.id || anio.Id}>
                        {anio.valor || anio.Valor}
                      </option>
                    ))}
                  </select>
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'materias' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear materia</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateMateria;
